import React, { useEffect } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { usePostHog } from "posthog-js/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { RoutesEnum, StatusStateEnum, TopicRetroPeriodEnum, TopicStatusEnum, TopicVisibilityEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import TopicForm from "~/forms/TopicForm";
import topicSchema from "~/forms/TopicForm/topicSchema";
import topicActionCreators from "~/store/topics/actions";
import topicSelectors from "~/store/topics/selectors";
import toastsActionCreators from "~/store/toasts/actions";
import { useHistory } from "react-router-dom";
import { Alert } from "~/components-shared";
import moment from "moment";
import TopicSteppedFlow from "~/components/TopicSteppedFlow";
import { useIntegratedSources } from "~/hooks/integrations";
import authSelectors from "~/store/auth/selectors";
const Sticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const TopicCreateModal = () => {
  const {
    t
  } = useTranslation();
  const action = useSelector(topicSelectors.getAction);
  const status = useSelector(topicSelectors.getStatusByTopic("+"));
  const error = useSelector(topicSelectors.getErrorByTopic("+"));
  const dispatch = useDispatch();
  const user = useSelector(authSelectors.getUser);
  const steppedFlow = true;
  const activeSources = useIntegratedSources();
  const history = useHistory();
  const posthog = usePostHog();
  const defaultValues = {
    name: "",
    status: TopicStatusEnum.ACTIVE,
    description: "",
    keywords: "",
    visibility: TopicVisibilityEnum.EVERYONE,
    ownerId: user?.id,
    memberIds: [],
    tags: [],
    startDate: moment().toISOString(),
    sources: activeSources,
    trigger: "",
    settings: {},
    retroPeriod: TopicRetroPeriodEnum.NOT_SET,
    markSlackAiDetections: false
  };
  const {
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(topicSchema),
    defaultValues
  });
  useEffect(() => {
    if (!action) {
      reset(defaultValues);
    }
  }, [action]);
  const onSubmit = handleSubmit(data => {
    dispatch(topicActionCreators.createStarted(data, _ref => {
      let {
        data: newTopic
      } = _ref;
      if (newTopic) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TopicCreateModal.topicCreated", {
            name: newTopic.name
          }),
          timeout: 10000,
          actions: [{
            icon: "external-link",
            onClick: () => history.push(RoutesEnum.TOPIC.replace(":topicId", newTopic.id))
          }]
        }));
        posthog.capture(`Topics Create – Save "${data?.name}"`, {
          ...data
        });
        reset(defaultValues);
      }
    }));
  });
  const onCancel = () => {
    dispatch(topicActionCreators.actionChanged(null, null));
    posthog.capture(`Topics Create – Cancel`);
  };
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("TopicCreateModal.createTopic"),
    onClose: onCancel,
    fullWidth: true,
    fullHeight: true,
    maxWidth: "1200px"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, steppedFlow ? /*#__PURE__*/React.createElement(TopicSteppedFlow, {
    control: control,
    watch: watch,
    trigger: trigger,
    onSubmit: onSubmit,
    status: status,
    setValue: setValue
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Sticky, null, /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error)), /*#__PURE__*/React.createElement(TopicForm, {
    control: control,
    watch: watch,
    trigger: trigger,
    topic: null,
    setValue: setValue
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: onCancel,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("TopicCreateModal.saveTopic"))))));
};
export default TopicCreateModal;